import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import Table from 'common/table/table';
import { Icon } from 'ui';
import { Icons, RowIconAction } from '../../../common/table/table-helper';
import { typeOfPartList, severityColorCodes } from '../../../utils/app-configs';

const Header = styled.div`
  color: ${p => p.theme.colors.alerts[p.severity]} !important;
  font-size: 14px;
  margin: 5px 0 10px 0;
  font-weight: bold;
  text-transform: uppercase;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[p.severity]} !important;
  margin-right: 5px;
`;

const supersededRowStyles = {
  margin: '0 0 10px 0',
  border: 0,
  overflow: 'hidden',
};

const SupersededList = ({
  originalPartId,
  autoSuperseded,
  supersededList,
  columns,
  isObsolete,
  partListType,
  handlePartAdd,
  handleRemovePart,
  handleRemoveSupersededPart,
  intl,
}) => {
  const deletePart = (partId) => {
    if (supersededList?.length === 1) {
      handleRemovePart(originalPartId);
    }
    handleRemoveSupersededPart(partId, originalPartId);
  };

  const addPart = (supersededPart) => {
    deletePart(supersededPart.id);
    handlePartAdd(supersededPart, originalPartId);
  };

  const supersededColumns = [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      renderFn: r => <Icons gap={10} >
        <RowIconAction
          iconKey='circle-plus'
          light={true}
          color='#6BC93C'
          action={() => {
            addPart(r);
            deletePart(r.id);
          }}
          disabled={false}
          fontSize={22}
        />
        <RowIconAction
          iconKey='circle-minus'
          light={true}
          color='#C93C3C'
          action={() => deletePart(r.id)}
          disabled={false}
          fontSize={22}
        />
      </Icons>,
    },
    ...columns,
  ];

  const getSupersededInfo = () => {
    if (supersededList?.length) {
      return (
        {
          icon: 'triangle-exclamation',
          severity: severityColorCodes.moderate,
          text: `${intl.formatMessage({ id: 'order.parts.supersededParts.REPLACE_PART' })}:`,
        }
      );
    }
    if (autoSuperseded?.originalPartNumber) {
      const { originalPartDescription, originalPartNumber, isAutoReplaced } = autoSuperseded;
      return {
        icon: 'square-info',
        severity: severityColorCodes.informational,
        text: `${isAutoReplaced ?
          intl.formatMessage({ id: 'order.parts.supersededParts.AUTO_REPLACE' }) :
          intl.formatMessage({ id: 'order.parts.supersededParts.MANUAL_REPLACE' })
          }"${originalPartDescription}"(${originalPartNumber})`,
      };
    }
    if (isObsolete) {
      return (
        {
          icon: 'triangle-exclamation',
          severity: severityColorCodes.high,
          text: partListType === typeOfPartList.xmlView ?
            intl.formatMessage(
              { id: 'order.parts.supersededParts.CANNOT_USE_OBSOLETE_PARTS_XML' },
            ) :
            intl.formatMessage({ id: 'order.parts.supersededParts.CANNOT_USE_OBSOLETE_PARTS' }),
        }
      );
    }
    return null;
  };

  const supersededInfo = getSupersededInfo();

  return <>
    <Header severity={supersededInfo?.severity}>
      <StyledIcon
        severity={supersededInfo?.severity}
        type='arrow-turn-down-right'
      />
      <StyledIcon
        severity={supersededInfo?.severity}
        type={supersededInfo?.icon}
      />
      {supersededInfo?.text}
    </Header>
    {supersededList?.length ? <Table
      extraRowStyles={supersededRowStyles}
      labelInColumn={true}
      theme='superseded'
      columns={supersededColumns}
      data={supersededList}
      fullWidth={true}
    /> : null}</>;
};

SupersededList.propTypes = {
  originalPartId: PropTypes.string,
  supersededList: PropTypes.array,
  autoSuperseded: PropTypes.object,
  columns: PropTypes.array,
  isObsolete: PropTypes.bool,
  partListType: PropTypes.string,
  handlePartAdd: PropTypes.func,
  handleRemovePart: PropTypes.func,
  handleRemoveSupersededPart: PropTypes.func,
  intl: PropTypes.object,
};

export default injectIntl(SupersededList);
